import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import styles from './styles.less'
import { T } from 'components/components/i18n'

const STORAGE_KEY = 'cookieConsentPreference'
const GLOBAL_STATUS_KEY = '__SHOP_COOKIE_CONSENT_STATUS__'
const GLOBAL_COPY_KEY = '__SHOP_COOKIE_CONSENT_COPY__'
const GLOBAL_SETTER_KEY = '__SET_SHOP_COOKIE_CONSENT_COPY__'
const GLOBAL_ENABLED_KEY = '__SHOP_COOKIE_CONSENT_ENABLED__'
const GLOBAL_ENABLED_SETTER_KEY = '__SET_SHOP_COOKIE_CONSENT_ENABLED__'
const COPY_EVENT = 'shop-cookie-consent-copy-update'
const ENABLE_EVENT = 'shop-cookie-consent-enabled-update'
const STATUS_EVENT = 'shop-cookie-consent-status-update'
const TEMPLATE_CONSENT_ATTR = 'data-cookie-consent'
const TEMPLATE_TARGET_ATTR = 'data-cookie-target'
const TEMPLATE_PROCESSED_ATTR = 'data-cookie-processed'
const TEMPLATE_CONSENT_APPROVED = 'approved'

const DEFAULT_COPY = {
  en: {
    title: 'We value your privacy',
    message: 'We use cookies to improve your browsing experience, display personalized ads or content, and analyze our traffic. By clicking Approve, you consent to our use of cookies.'
  },
  sv: {
    title: 'Vi värdesätter din integritet',
    message: 'Vi använder cookies för att förbättra din upplevelse, visa anpassade annonser eller innehåll och analysera vår trafik. Genom att klicka på Godkänn samtycker du till vår användning av cookies.'
  },
  nb: {
    title: 'Vi verdsetter personvernet ditt',
    message: 'Vi bruker informasjonskapsler for å forbedre opplevelsen din, vise tilpassede annonser eller innhold og analysere trafikken vår. Ved å klikke på Godkjenn godtar du vår bruk av informasjonskapsler.'
  }
}

const hasLocalStorage = () => {
  try {
    const testKey = '__cookieConsent__'
    window.localStorage.setItem(testKey, testKey)
    window.localStorage.removeItem(testKey)
    return true
  } catch (error) {
    if (process.env.NODE_ENV === 'development')
      console.warn('Unable to use localStorage for cookie consent', error) // eslint-disable-line no-console
    return false
  }
}

const readStoredStatus = () => {
  if (typeof window === 'undefined' || !hasLocalStorage())
    return null
  const value = window.localStorage.getItem(STORAGE_KEY)
  return value === 'approved' || value === 'denied' ? value : null
}

const updateStoredStatus = (status) => {
  if (typeof window === 'undefined' || !hasLocalStorage())
    return
  window.localStorage.setItem(STORAGE_KEY, status)
}

const getOverrideCopy = () => {
  if (typeof window === 'undefined')
    return null
  const overrides = window[GLOBAL_COPY_KEY]
  if (overrides && typeof overrides === 'object')
    return overrides
  return null
}

const resolveCopy = (locale, overrides) => {
  const activeLocale = DEFAULT_COPY[locale] ? locale : 'en'
  const base = DEFAULT_COPY[activeLocale]

  if (!overrides)
    return base

  const overrideLocale = overrides[activeLocale] || {}
  const fallback = overrides.en || {}

  return {
    title: overrideLocale.title || fallback.title || base.title,
    message: overrideLocale.message || fallback.message || base.message
  }
}

const setGlobalStatus = (status) => {
  if (typeof window === 'undefined')
    return
  window[GLOBAL_STATUS_KEY] = status
  let event
  try {
    event = new CustomEvent(STATUS_EVENT, { detail: status })
  } catch (error) {
    if (typeof document !== 'undefined') {
      event = document.createEvent('CustomEvent')
      event.initCustomEvent(STATUS_EVENT, false, false, status)
    }
  }
  if (event)
    window.dispatchEvent(event)
}

const getGlobalEnabled = () => {
  if (typeof window === 'undefined')
    return false

  if (typeof window[GLOBAL_ENABLED_KEY] === 'boolean')
    return window[GLOBAL_ENABLED_KEY]

  return false
}

const ensureGlobalCopySetter = () => {
  if (typeof window === 'undefined')
    return

  if (typeof window[GLOBAL_SETTER_KEY] !== 'function') {
    window[GLOBAL_SETTER_KEY] = (copy) => {
      window[GLOBAL_COPY_KEY] = copy
      let event
      try {
        event = new CustomEvent(COPY_EVENT)
      } catch (error) {
        if (typeof document !== 'undefined') {
          event = document.createEvent('CustomEvent')
          event.initCustomEvent(COPY_EVENT, false, false, undefined)
        }
      }
      if (event)
        window.dispatchEvent(event)
    }
  }
}

const ensureGlobalEnabledSetter = () => {
  if (typeof window === 'undefined')
    return

  if (typeof window[GLOBAL_ENABLED_SETTER_KEY] !== 'function') {
    window[GLOBAL_ENABLED_SETTER_KEY] = (nextEnabled) => {
      const value = !!nextEnabled
      window[GLOBAL_ENABLED_KEY] = value
      if (value)
        setGlobalStatus(readStoredStatus() || 'pending')
      else
        setGlobalStatus('disabled')
      let event
      try {
        event = new CustomEvent(ENABLE_EVENT)
      } catch (error) {
        if (typeof document !== 'undefined') {
          event = document.createEvent('CustomEvent')
          event.initCustomEvent(ENABLE_EVENT, false, false, undefined)
        }
      }
      if (event)
        window.dispatchEvent(event)
    }
  }
}

const copyScriptAttributes = (source, target) => {
  Array.from(source.attributes).forEach(attr => {
    if (attr.name === TEMPLATE_CONSENT_ATTR || attr.name === TEMPLATE_TARGET_ATTR || attr.name === TEMPLATE_PROCESSED_ATTR)
      return
    if (attr.name === 'type' && attr.value === 'text/plain')
      return
    target.setAttribute(attr.name, attr.value)
  })
}

const injectExternalScript = (element, target) => {
  const script = document.createElement('script')
  copyScriptAttributes(element, script)
  script.async = element.hasAttribute('async')
  script.defer = element.hasAttribute('defer')

  const src = element.getAttribute('src')
  if (src)
    script.src = src

  target.appendChild(script)
}

const injectInlineScripts = (element, target) => {
  const script = document.createElement('script')
  copyScriptAttributes(element, script)
  const raw = element.textContent || ''
  const unwrapped = raw.replace(/^\s*<!--([\s\S]*?)-->\s*$/, '$1')
  script.text = unwrapped
  target.appendChild(script)
}

const cloneTemplateChildren = (template) => {
  if (template?.childNodes?.length) {
    return Array.from(template.childNodes)
  }

  if ('content' in template && template.content) {
    const fragment = template.content.cloneNode(true)
    return Array.from(fragment.childNodes)
  }

  const html = template.innerHTML
  if (html && typeof document !== 'undefined') {
    const range = document.createRange()
    if (document.body)
      range.selectNode(document.body)
    const fragment = range.createContextualFragment(html)
    return Array.from(fragment.childNodes)
  }

  return []
}

const appendChildNode = (node, target) => {
  if (node.nodeType === 1 && node.tagName === 'SCRIPT') {
    if (node.src && (!node.textContent || !node.textContent.trim()))
      injectExternalScript(node, target)
    else
      injectInlineScripts(node, target)
    return
  }

  if (node.nodeType === 1 && node.tagName === 'TEMPLATE') {
    const nestedChildren = cloneTemplateChildren(node)
    nestedChildren.forEach(child => appendChildNode(child, target))
    return
  }

  if (node.nodeType === 1) {
    target.appendChild(node.cloneNode(true))
    return
  }

  if (node.nodeType === 3) {
    if (node.textContent && node.textContent.trim())
      target.appendChild(document.createTextNode(node.textContent))
  }
}

const processTemplateElement = (template, target) => {
  const children = cloneTemplateChildren(template)
  children.forEach(node => appendChildNode(node, target))
}

const runConsentTemplates = () => {
  if (typeof document === 'undefined')
    return

  const nodes = document.querySelectorAll(`template[${TEMPLATE_CONSENT_ATTR}="${TEMPLATE_CONSENT_APPROVED}"]`)

  nodes.forEach(node => {
    if (node.getAttribute(TEMPLATE_PROCESSED_ATTR) === 'true')
      return

    const targetSelector = node.getAttribute(TEMPLATE_TARGET_ATTR)
    const target = targetSelector ? document.querySelector(targetSelector) : (document.head || document.body)
    if (!target)
      return

    processTemplateElement(node, target)

    node.setAttribute(TEMPLATE_PROCESSED_ATTR, 'true')
  })
}

if (typeof window !== 'undefined') {
  const storedStatus = readStoredStatus()
  const defaultEnabled = typeof window[GLOBAL_ENABLED_KEY] === 'boolean' ? window[GLOBAL_ENABLED_KEY] : false
  window[GLOBAL_ENABLED_KEY] = defaultEnabled
  if (defaultEnabled)
    setGlobalStatus(storedStatus || 'pending')
  else
    setGlobalStatus('disabled')
  ensureGlobalCopySetter()
  ensureGlobalEnabledSetter()
  if (typeof window.__RUN_COOKIE_CONSENT_TEMPLATES__ !== 'function')
    window.__RUN_COOKIE_CONSENT_TEMPLATES__ = runConsentTemplates
}

export const CookieConsent = ({ locale }) => {
  const [status, setStatus] = useState(() => readStoredStatus())
  const [isEnabled, setIsEnabled] = useState(() => getGlobalEnabled())
  const [overrideCopy, setOverrideCopy] = useState(getOverrideCopy())

  useEffect(() => {
    if (!isEnabled) {
      setGlobalStatus('disabled')
      return
    }

    if (status === 'approved' || status === 'denied')
      setGlobalStatus(status)
    else
      setGlobalStatus('pending')
  }, [isEnabled, status])

  useEffect(() => {
    if (!isEnabled || status !== 'approved' || typeof document === 'undefined')
      return

    runConsentTemplates()

    const head = document.head
    const body = document.body
    if (!head && !body)
      return

    const observerTarget = head || body
    if (typeof MutationObserver === 'undefined')
      return

    const observer = new MutationObserver(() => runConsentTemplates())
    observer.observe(observerTarget, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [isEnabled, status])

  useEffect(() => {
    if (typeof window === 'undefined')
      return

    const handleOverrideUpdate = () => {
      setOverrideCopy(getOverrideCopy())
    }

    window.addEventListener(COPY_EVENT, handleOverrideUpdate)
    return () => window.removeEventListener(COPY_EVENT, handleOverrideUpdate)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined')
      return

    const handleEnabledUpdate = () => {
      setIsEnabled(getGlobalEnabled())
    }

    window.addEventListener(ENABLE_EVENT, handleEnabledUpdate)
    return () => window.removeEventListener(ENABLE_EVENT, handleEnabledUpdate)
  }, [])

  const handleChoice = useCallback((nextStatus) => {
    setStatus(nextStatus)
    updateStoredStatus(nextStatus)
  }, [])

  const copy = useMemo(() => resolveCopy(locale, overrideCopy), [locale, overrideCopy])

  if (!isEnabled)
    return null

  if (status === 'approved' || status === 'denied')
    return null

  return (
    <div className={styles.wrapper} role="dialog" aria-live="polite" aria-label={copy.title}>
      <div className={styles.container}>
        <h2 className={styles.title}>{copy.title}</h2>
        <p className={styles.message}>{copy.message}</p>
        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.button} ${styles.deny}`}
            onClick={() => handleChoice('denied')}
          >
            <T id="Deny" />
          </button>
          <button
            type="button"
            className={`${styles.button} ${styles.approve}`}
            onClick={() => handleChoice('approved')}
          >
            <T id="Approve" />
          </button>
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  locale: state?.intl?.locale || 'en'
})

export default connect(mapStateToProps)(CookieConsent)
